"use client";

import { 
  QueryClient, 
  QueryClientProvider,
  QueryCache,
  MutationCache,
} from "@tanstack/react-query";
import dynamic from "next/dynamic";
import { useState, ReactNode } from "react";
import { useAuth, useOrganization } from "@clerk/nextjs";
import { logQueryError, stringifyQueryKey, categorizeError } from "@/lib/query-error-logger";
import { recordQueryError, checkErrorAlerts } from "@/lib/error-metrics";
import { toast } from "sonner";

const ReactQueryDevtools = dynamic(
  () => import("@tanstack/react-query-devtools").then((m) => m.ReactQueryDevtools),
  { ssr: false }
);

export function QueryProvider({ children }: { children: ReactNode }) {
  const { userId } = useAuth();
  const { organization } = useOrganization();

  const [queryClient] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (error, query) => {
            // Log error for production monitoring
            logQueryError(query.queryKey, error, {
              userId: userId || undefined,
              orgId: organization?.id || undefined,
              retryCount: query.state.fetchFailureCount,
            });

            // Record for metrics tracking
            const errorType = categorizeError(error);
            recordQueryError(
              errorType,
              stringifyQueryKey(query.queryKey),
              (error as any)?.status
            );

            // Check if we should alert
            const alertStatus = checkErrorAlerts();
            if (alertStatus.alert && alertStatus.level === 'critical') {
              console.error('🚨 Critical error threshold reached:', alertStatus.reason);
            }
          },
        }),
        mutationCache: new MutationCache({
          onError: (error, _variables, _context, mutation) => {
            // Log mutation errors
            const mutationKey = (mutation.options as any).mutationKey || ['mutation'];
            logQueryError(mutationKey, error, {
              userId: userId || undefined,
              orgId: organization?.id || undefined,
              retryCount: mutation.state.failureCount,
            });

            // Record for metrics
            const errorType = categorizeError(error);
            recordQueryError(
              errorType,
              stringifyQueryKey(mutationKey),
              (error as any)?.status
            );

            // Show user-friendly error toast for mutations
            if (errorType !== 'network' && errorType !== 'aborted') {
              toast.error(
                'An error occurred while saving your changes. Please try again.',
                { duration: 4000 }
              );
            }
          },
        }),
        defaultOptions: {
          queries: {
            // Stale time: data is fresh for 5 minutes
            staleTime: 5 * 60 * 1000,
            // Cache time: keep unused data in cache for 10 minutes
            gcTime: 10 * 60 * 1000,
            // Retry with sophisticated error handling
            retry: (failureCount, error: any) => {
              const errorType = categorizeError(error);
              
              // Don't retry auth errors (will redirect)
              if (errorType === 'authentication' || errorType === 'authorization') {
                return false;
              }
              
              // Don't retry validation errors
              if (errorType === 'validation') {
                return false;
              }
              
              // Don't retry aborted requests
              if (errorType === 'aborted') {
                return false;
              }
              
              // Retry network and server errors
              if (errorType === 'network' || errorType === 'server') {
                return failureCount < 3;
              }
              
              // Default: retry once for unknown errors
              return failureCount < 1;
            },
            retryDelay: (attemptIndex, error: any) => {
              // Use Retry-After header if present (for 429 rate limits)
              const retryAfter = (error as any)?.response?.headers?.['retry-after'];
              if (retryAfter) {
                const delay = parseInt(retryAfter, 10);
                return !isNaN(delay) ? delay * 1000 : 30000;
              }
              
              // Exponential backoff with max 30 seconds
              return Math.min(1000 * 2 ** attemptIndex, 30000);
            },
            // Refetch on window focus to keep data fresh
            refetchOnWindowFocus: true,
            // Refetch on reconnect
            refetchOnReconnect: true,
            // Network mode: online mode ensures fresh data for critical operations
            networkMode: 'online',
          },
          mutations: {
            // Retry mutations with careful handling
            retry: (failureCount, error: any) => {
              const errorType = categorizeError(error);
              
              // Never retry auth/validation errors for mutations
              if (
                errorType === 'authentication' || 
                errorType === 'authorization' || 
                errorType === 'validation'
              ) {
                return false;
              }
              
              // Retry network errors once
              if (errorType === 'network') {
                return failureCount < 1;
              }
              
              // Retry server errors with caution (once)
              if (errorType === 'server' && failureCount < 1) {
                return true;
              }
              
              return false;
            },
            retryDelay: (attemptIndex) => Math.min(2000 * 2 ** attemptIndex, 10000),
            // Network mode for mutations
            networkMode: 'online',
          },
        },
      })
  );

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {/* React Query DevTools - only visible in development */}
      {process.env.NODE_ENV === 'development' && (
        <ReactQueryDevtools 
          initialIsOpen={false}
          position="bottom-right"
          buttonPosition="bottom-right"
        />
      )}
    </QueryClientProvider>
  );
}